import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Hand, CheckCircle2, Archive, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";
import { PRIORITIES, TICKET_STATUS, fmtDT, OPS } from "@/lib/constants";
import { useHotel } from "@/context/HotelContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { PriorityBadge, StatusBadge } from "@/components/Badges";
import { ResourceForm } from "@/components/ResourceForm";

const FIELDS = [
  { key: "hotel_id", label: "Struttura", type: "hotel", required: true },
  { key: "priority", label: "Priorità", type: "select", required: true, options: Object.entries(PRIORITIES).map(([k, v]) => ({ value: k, label: `${v.label} (presa in carico ${v.sla < 1440 ? `≤ ${v.sla} min` : "entro la giornata"})` })) },
  { key: "title", label: "Oggetto", required: true, full: true },
  { key: "category", label: "Tipologia", type: "select", required: true, options: [{ value: "ordinaria", label: "Richiesta ordinaria" }, { value: "extra", label: "Servizio extra" }, { value: "reclamo", label: "Reclamo" }, { value: "operativa", label: "Segnalazione operativa" }] },
  { key: "assignee", label: "Assegnato a" },
  { key: "description", label: "Descrizione", type: "textarea" },
];

const useNow = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  return now;
};

const SlaTimer = ({ t, now }) => {
  const sla = PRIORITIES[t.priority]?.sla || 60;
  const created = new Date(t.created_at).getTime();
  if (t.taken_at) {
    const m = (new Date(t.taken_at).getTime() - created) / 60000;
    const ok = m <= sla;
    return <span className={`font-mono text-xs ${ok ? "text-emerald-600" : "text-red-600"}`} data-testid="ticket-sla-result">Preso in {m < 1 ? "<1" : Math.round(m)} min {ok ? "✓ SLA" : "· SLA superato"}</span>;
  }
  const left = created + sla * 60000 - now;
  const abs = Math.abs(left) / 1000;
  const h = Math.floor(abs / 3600), mm = Math.floor((abs % 3600) / 60), ss = Math.floor(abs % 60);
  const txt = `${h ? `${h}h ` : ""}${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  return (
    <span data-testid={`ticket-${t.priority.toLowerCase()}-sla-timer`} className={`inline-flex items-center gap-1 font-mono text-xs ${left < 0 ? "text-red-600" : left < 120000 ? "text-amber-600" : "text-slate-700"}`}>
      <Timer className="w-3 h-3" />{left < 0 ? `SLA superato da ${txt}` : `Presa in carico entro ${txt}`}
    </span>
  );
};

const TicketRow = ({ t, now, hotelName, canAct, onStatus }) => (
  <div className={`panel p-4 flex flex-col md:flex-row md:items-center gap-3 justify-between ${t.priority === "P1" && t.status === "aperto" ? "border-red-500/50" : ""}`} data-testid="ticket-row">
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2 mb-1.5">
        <PriorityBadge p={t.priority} /><StatusBadge value={t.status} />
        <span className="badge-x bg-slate-100 border-slate-200 text-slate-500">{t.category}</span>
      </div>
      <div className="text-slate-900 font-medium" data-testid="ticket-title">{t.title}</div>
      <div className="text-xs text-slate-500 mt-1">{hotelName(t.hotel_id)} · aperto {fmtDT(t.created_at)} da {t.created_by || "—"} {t.assignee && `· assegnato a ${t.assignee}`}</div>
      {t.description && <div className="text-sm text-slate-500 mt-1.5">{t.description}</div>}
    </div>
    <div className="flex flex-col md:items-end gap-2 shrink-0">
      <SlaTimer t={t} now={now} />
      {canAct && (
        <div className="flex gap-2">
          {t.status === "aperto" && <Button size="sm" onClick={() => onStatus(t, "in_carico")} className="gap-1" data-testid="ticket-take-btn"><Hand className="w-3.5 h-3.5" />Prendi in carico</Button>}
          {t.status === "in_carico" && <Button size="sm" variant="outline" onClick={() => onStatus(t, "risolto")} className="gap-1" data-testid="ticket-resolve-btn"><CheckCircle2 className="w-3.5 h-3.5" />Risolvi</Button>}
          {t.status === "risolto" && <Button size="sm" variant="ghost" onClick={() => onStatus(t, "chiuso")} className="gap-1" data-testid="ticket-close-btn"><Archive className="w-3.5 h-3.5" />Chiudi</Button>}
        </div>
      )}
    </div>
  </div>
);

export default function Tickets() {
  const { hotelId, hotelName } = useHotel();
  const { user } = useAuth();
  const now = useNow();
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("attivi");
  const [prio, setPrio] = useState("all");
  const [creating, setCreating] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/tickets", { params: hotelId !== "all" ? { hotel_id: hotelId } : {} });
    setRows(data);
  }, [hotelId]);
  useEffect(() => { load(); }, [load]);

  const list = useMemo(() => rows
    .filter((t) => (tab === "attivi" ? ["aperto", "in_carico"].includes(t.status) : tab === "chiusi" ? ["risolto", "chiuso"].includes(t.status) : true))
    .filter((t) => prio === "all" || t.priority === prio)
    .sort((a, b) => (tab === "attivi" ? a.priority.localeCompare(b.priority) || a.created_at.localeCompare(b.created_at) : b.created_at.localeCompare(a.created_at))), [rows, tab, prio]);

  const setStatus = async (t, status) => {
    try { await api.put(`/tickets/${t.id}`, { status }); toast.success(`Ticket ${TICKET_STATUS[status].toLowerCase()}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const create = async (v) => {
    try { await api.post("/tickets", v); toast.success("Ticket aperto"); setCreating(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <PageHeader eyebrow="Servizio Clienti h24" title="Ticket & SLA" subtitle="P1 ≤ 5 min · P2 ≤ 15 min · P3 ≤ 60 min · P4 entro la giornata. Escalation automatica secondo matrice.">
        <Button className="gap-2" onClick={() => setCreating({ hotel_id: hotelId !== "all" ? hotelId : undefined, priority: "P3", category: "ordinaria" })} data-testid="new-ticket-btn"><Plus className="w-4 h-4" />Nuovo ticket</Button>
      </PageHeader>
      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="attivi" data-testid="tickets-tab-active">Attivi ({rows.filter((t) => ["aperto", "in_carico"].includes(t.status)).length})</TabsTrigger>
            <TabsTrigger value="chiusi" data-testid="tickets-tab-closed">Risolti/Chiusi</TabsTrigger>
            <TabsTrigger value="tutti" data-testid="tickets-tab-all">Tutti</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex gap-1.5 flex-wrap">
          {["all", "P1", "P2", "P3", "P4"].map((p) => (
            <button key={p} onClick={() => setPrio(p)} data-testid={`tickets-filter-${p.toLowerCase()}`}
              className={`px-3 py-1 rounded-full text-xs font-mono border transition-colors ${prio === p ? "bg-amber-600 text-white border-amber-500" : "border-slate-300 text-slate-500 hover:border-slate-400"}`}>
              {p === "all" ? "Tutte" : p}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        {!list.length && <div className="panel p-10 text-center text-slate-500" data-testid="tickets-empty">Nessun ticket</div>}
        {list.map((t) => <TicketRow key={t.id} t={t} now={now} hotelName={hotelName} canAct={OPS.includes(user.role)} onStatus={setStatus} />)}
      </div>
      <ResourceForm open={!!creating} initial={creating} onClose={() => setCreating(null)} onSubmit={create} fields={FIELDS} title="Nuovo ticket" testid="ticket-form" />
    </>
  );
}
